import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import { useGetTunnelMessagesQuery, useUpdateSubscriptionAlertMutation } from 'api/main';
import {
    alertMessagesAdmin,
    RoleNames,
    completedFormTexts,
    roleCategories,
    environments,
} from 'utils/constants';
import {
    jsonParseSafe,
    parseChoice,
    parseFullName,
    parseKnowledgeLevels,
    parseSlpKnowledgeLevels,
} from 'utils/helpers';
import { useUserRoleMatchCategory } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import { ActionButton, CustomTextField, SubscriptionAlertCircle } from 'components';
import { Box, Typography } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';

const parseKnowledgeRating = rating => {
    if (rating === 'Weak') {
        return 'faible';
    } else if (rating === 'Intermediary') {
        return 'Intermédiaire';
    } else {
        return 'Haute';
    }
};

const parseAdditionalInfo = (info, messages = {}) => {
    try {
        const {
            warningCode,
            field,
            knowledgeLevels,
            slpKnowledgeLevel1,
            slpKnowledgeLevel2,
            value,
            shortTermLiquidity,
            name,
            choice,
            other,
            rating,
            bicVerified,
            bicInput,
        } = jsonParseSafe(info) || {};

        if (warningCode) {
            return warningCode;
        }

        if (field) {
            return field;
        }

        if (knowledgeLevels) {
            return parseKnowledgeLevels(knowledgeLevels);
        }

        if (slpKnowledgeLevel1) {
            return { level_1: parseSlpKnowledgeLevels(slpKnowledgeLevel1, 1), rating };
        }

        if (slpKnowledgeLevel2) {
            return { level_2: parseSlpKnowledgeLevels(slpKnowledgeLevel2, 2), rating };
        }

        if (value) {
            return value;
        }

        if (choice) {
            return parseChoice(choice, other, messages);
        }

        if (shortTermLiquidity) {
            return shortTermLiquidity;
        }

        if (name) {
            return name;
        }

        if (bicInput || bicVerified) {
            return { bicVerified, bicInput };
        }

        return null;
    } catch (error) {
        if (process.env.REACT_APP_ENV !== environments.PROD) {
            console.log(error);
        }
        return null;
    }
};

const getAlertMessage = (alert, status, roleName, messages) => {
    if (alert.alertCode === 121) {
        // these are custom alerts with a user defined message
        const info = jsonParseSafe(alert.additionalInfo);
        if (info?.total_score) {
            return `${alert.message}\xa0(${info.total_score}\xa0/\xa0${info.max_score})`;
        }
        // return older simple alert message without scoring
        return alert.message;
    }

    switch (alert.message) {
        case 'id_check_secondary.transaction.initiated':
            return alertMessagesAdmin[alert.message](status);
        case 'id_check.empty_response':
        case 'id_check_secondary.empty_response':
        case 'rib_check.bic_mismatch':
        case 'address_check.empty_response':
        case 'bank_statement_check.empty_response':
        case 'user_knowledge.knowledge_origin':
            return (
                alertMessagesAdmin[alert.message](
                    parseAdditionalInfo(alert.additionalInfo, messages)
                ) || '-'
            );
        case 'rib.response.warning':
        case 'kyc.short_term_liquidity':
            return parseAdditionalInfo(alert.additionalInfo);
        case 'ABANDONED_STEP':
            return alertMessagesAdmin[alert.message](alert.stepId);
        case 'user_knowledge.level_warning':
            const knowledgeLevels = parseAdditionalInfo(alert.additionalInfo) || ['-', '-'];
            return alertMessagesAdmin[alert.message](...knowledgeLevels);
        case 'user_knowledge.slp.level1.score': {
            const slpKnowledgeLevel1 = parseAdditionalInfo(alert.additionalInfo) || ['-'];
            return alertMessagesAdmin[alert.message](
                slpKnowledgeLevel1.level_1,
                parseKnowledgeRating(slpKnowledgeLevel1.rating)
            );
        }
        case 'user_knowledge.slp.level2.score': {
            const slpKnowledgeLevel2 = parseAdditionalInfo(alert.additionalInfo) || ['-'];
            return alertMessagesAdmin[alert.message](
                slpKnowledgeLevel2.level_2,
                parseKnowledgeRating(slpKnowledgeLevel2.rating)
            );
        }
        case 'kyc.other_expenses':
            return alertMessagesAdmin[alert.message](parseAdditionalInfo(alert.additionalInfo));
        case 'comply_advantage.search_investor':
        case 'comply_advantage.search_beneficiaries':
            return alertMessagesAdmin[alert.message];
        case 'kyc.heritage.greater_than_fifteen_percent_of_investing_sum':
        case 'kyc.indebtedness_heritage_ratio':
            return alertMessagesAdmin[alert.message](roleName === RoleNames.PP_PROFESSIONNEL);
        case 'laundering.beneficiary_vigilance.empty_nif':
        case 'rib_check.iban.empty_response':
            return alertMessagesAdmin[alert.message](parseAdditionalInfo(alert.additionalInfo));
        default:
            return alertMessagesAdmin[alert.message];
    }
};

const useStyles = makeStyles(theme => ({
    saveAlertButton: {
        padding: '2px 5px',
        borderRadius: '5px',
        flex: '1 1',
    },
    removeAlertButton: {
        padding: '2px 5px',
        borderRadius: '5px',
    },
    commentField: {
        height: 120,
        flex: '1 1 65%',
        overflow: 'hidden',
        marginRight: theme.spacing(),
        marginBottom: 0,
    },
    alertMessage: {
        marginLeft: theme.spacing(3),
        flex: '1 1 60%',
    },
    alertMessageFull: {
        marginLeft: theme.spacing(3),
    },
    alertComment: {
        marginLeft: theme.spacing(3),
        width: '70%',
    },
}));

const Alert = ({
    alert,
    secondIdCheckStatus,
    onAlertChange,
    isRemovable,
    roleName,
    subscription,
    hideComment,
}) => {
    const classes = useStyles();
    const userIsProvider = useUserRoleMatchCategory(roleCategories.PROVIDERS);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);

    const [updateAlert, { isLoading, isSuccess, isError, data }] =
        useUpdateSubscriptionAlertMutation();

    const [editing, setEditing] = useState(false);
    const [comment, setComment] = useState('');
    const [commentError, setCommentError] = useState('');
    const { data: messages } = useGetTunnelMessagesQuery();

    const solverUser = useMemo(() => {
        if (alert.solverUser?.firstName && alert.solverUser?.lastName) {
            return parseFullName(alert.solverUser);
        }
        return '-';
    }, [alert]);

    const handleEnterEditing = useCallback(() => setEditing(true), []);
    const handleCommentChange = useCallback(e => {
        setCommentError('');
        setComment(e.target.value);
    }, []);

    const saveComment = useCallback(() => {
        updateAlert({ id: alert.id, comment, solved: true });
    }, [comment, alert, updateAlert]);

    const experienceAlert = useMemo(() => {
        const nbTransactions = subscription?.kycSurvey?.transactionsCarriedOut || 0;
        const nonCoteChoice =
            messages?.[subscription?.kycSurvey?.unlistedShareInvestment] ||
            messages?.['financial_instruments_options.no_orders'];
        const partsChoice =
            messages?.[subscription?.kycSurvey?.otherFundInvestment] ||
            messages?.['financial_instruments_options.no_orders'];
        const allInvestments = {
            opcvmActionInvestment: subscription?.kycSurvey?.opcvmActionInvestment,
            opcvmObligationInvestment: subscription?.kycSurvey?.opcvmObligationInvestment,
            opcvmMonetaryInvestment: subscription?.kycSurvey?.opcvmMonetaryInvestment,
            listedShareInvestment: subscription?.kycSurvey?.listedShareInvestment,
            unlistedShareInvestment: subscription?.kycSurvey?.unlistedShareInvestment,
            otherFundInvestment: subscription?.kycSurvey?.otherFundInvestment,
            otherInvestment: subscription?.kycSurvey?.otherInvestment,
        };
        const selectedInvestments = Object.keys(allInvestments)
            .map(investment => {
                if (allInvestments[investment] !== 'financial_instruments_options.no_orders') {
                    return completedFormTexts.form_5.choice[investment];
                }
                return null;
            })
            .filter(investment => investment)
            .join(', ');

        switch (alert.message) {
            case 'experience.transactions_number_inconsistence_1':
            case 'experience.transactions_number_inconsistence_2':
                return (
                    <React.Fragment>
                        <Typography className={classes.alertMessage}>
                            {texts.EXPERIENCE_INCONSISTENCY}
                        </Typography>
                        <ul>
                            <li>
                                <Typography className={classes.alertMessage}>
                                    {texts.EXPERIENCE_INCONSISTENCY_OPT_1(nbTransactions)}
                                </Typography>
                            </li>
                            <li>
                                <Box>
                                    <Typography className={classes.alertMessage}>
                                        {texts.EXPERIENCE_INCONSISTENCY_OPT_2}
                                    </Typography>
                                    <Typography className={classes.alertMessage}>
                                        {texts.EXPERIENCE_INCONSISTENCY_OPT_2_1(nonCoteChoice)}
                                    </Typography>
                                    <Typography className={classes.alertMessage}>
                                        {texts.EXPERIENCE_INCONSISTENCY_OPT_2_2(partsChoice)}
                                    </Typography>
                                </Box>
                            </li>
                        </ul>
                    </React.Fragment>
                );
            case 'kyc.financial_investments_inexperienced':
                return (
                    <Typography className={classes.alertMessage}>
                        {texts.KYC_INVESTMENTS_INEXPERIENCED(selectedInvestments)}
                    </Typography>
                );
            default:
                return null;
        }
    }, [alert, classes, messages, subscription]);

    const alertMessage = getAlertMessage(alert, secondIdCheckStatus, roleName, messages);

    useEffect(() => {
        if (isSuccess) {
            setCommentError('');
            setEditing(false);
            onAlertChange();
        }
    }, [isSuccess, onAlertChange]);

    useEffect(() => {
        if (isError && data?.status === 400) {
            setCommentError(texts.MAX_CHARACTER_ERROR);
        }
    }, [isError, data]);

    if (!isRemovable) {
        return (
            <Box mb={1} position="relative" display="flex" flexDirection="column">
                <SubscriptionAlertCircle
                    positionAbsolute
                    withTopMargin
                    alertType={alert.type}
                    alertSolved={alert.solved}
                />{' '}
                {alertMessage ? (
                    <Typography className={classes.alertMessageFull}>{alertMessage}</Typography>
                ) : (
                    experienceAlert
                )}
            </Box>
        );
    }

    return (
        <Box mb={1} position="relative" display="flex" flexDirection="column">
            <Box mb={1} display="flex" alignItems="center" justifyContent="space-between">
                <SubscriptionAlertCircle
                    positionAbsolute
                    alertType={alert.type}
                    alertSolved={alert.solved}
                />{' '}
                {alertMessage ? (
                    <Typography className={classes.alertMessage}>{alertMessage}</Typography>
                ) : (
                    experienceAlert
                )}
                {!alert.solved && !userIsProvider && !userIsPromoter && (
                    <ActionButton
                        onClick={handleEnterEditing}
                        variant="outlined"
                        component="span"
                        classes={{ root: classes.removeAlertButton }}
                    >
                        {texts.ALERTS.SOLVE_ALERT}
                    </ActionButton>
                )}
            </Box>
            {editing && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <CustomTextField
                        multiline
                        rows={3}
                        error={commentError}
                        showError
                        inputStyle={classes.commentField}
                        classes={{ root: classes.commentField }}
                        value={comment}
                        onChange={handleCommentChange}
                    />
                    <ActionButton
                        variant="outlined"
                        component="span"
                        onClick={saveComment}
                        className={classes.saveAlertButton}
                        disabled={isLoading || comment.trim() === ''}
                    >
                        {texts.SAVE_AND_CONTINUE}
                    </ActionButton>
                </Box>
            )}
            {alert.solved && !hideComment && (
                <Typography variant="body2" className={classes.alertComment}>
                    {`${alert.comment || '-'} | ${solverUser} | ${moment(alert.updatedAt).format(
                        'DD.MM.YYYY'
                    )}`}
                </Typography>
            )}
        </Box>
    );
};

Alert.propTypes = {
    alert: PropTypes.object,
    secondIdCheckStatus: PropTypes.string,
    onAlertChange: PropTypes.func,
    isRemovable: PropTypes.bool,
    roleName: PropTypes.string,
    subscription: PropTypes.object,
    hideComment: PropTypes.bool,
};

export default Alert;
